/**
 * The factor model's style factors, apart from its sector betas — pure, no fetching.
 *
 * The multifactor run prices a stock against the style factors (momentum,
 * value, quality, size, growth, volatility) AND against each sector index
 * (`sector_utilities_score`, `sector_energy_score`, …). A sector beta is a
 * sector bet, not a style, and eleven of them as factor columns bury the tilts a
 * manager is asked about. So the columns are the styles, and what the sector
 * (and any industry) betas produced is folded into the residual — at the fund
 * level and on every stock — so a row still adds up to its active return and
 * nothing a sector did is silently dropped.
 */

export const isStyleFactor = (name) => !/^(sector|industry)_/i.test(String(name || ''));

/**
 * The style loadings a factor panel draws — label → exposures field, in bar
 * order. The one list every panel (scheme, model, client portfolio, benchmark
 * marker, history chart) reads, so they cannot disagree.
 *
 * Momentum is two entries: the run publishes it short- and long-term and leaves
 * the combined `momentumScore` null on every record. Low and high volatility are
 * separate factors with their own indices, not two ends of one scale, so both
 * are drawn. Size is not drawn.
 */
export const FACTOR_FIELDS = {
  'ST momentum': 'shortTermMomentumScore',
  'LT momentum': 'longTermMomentumScore',
  Value: 'valueScore',
  Quality: 'qualityScore',
  Growth: 'growthScore',
  'Low volatility': 'lowVolatilityScore',
  'High volatility': 'highVolatilityScore',
};

const finite = (v) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);

/**
 * One row per regression factor, each carrying all three readings — the one
 * place the API's two kinds of number are told apart:
 *   - every `*Score` in `factorExposures` is a TILT against the Top-500 index
 *     (subject − index); the subject's raw loading is tilt + the index's;
 *   - `beta` is RAW (0.84 against the index's 1.0) — its tilt is the gap.
 * Each row has `tilt`, `raw` and `index`, null where the index publishes
 * nothing to work one out from. Beta first, then FACTOR_FIELDS in order.
 */
export function regressionRows(factorExposures, benchmark) {
  if (!factorExposures) return [];
  const rows = [];
  const beta = finite(factorExposures.beta);
  if (beta != null) {
    const index = finite(benchmark?.beta);
    rows.push({ label: 'Beta', raw: beta, index, tilt: index == null ? null : beta - index, isBeta: true });
  }
  Object.entries(FACTOR_FIELDS).forEach(([label, key]) => {
    const tilt = finite(factorExposures[key]);
    if (tilt == null) return;
    const index = finite(benchmark?.[key]);
    rows.push({ label, tilt, index, raw: index == null ? null : tilt + index });
  });
  return rows;
}

/**
 * The fundamental style model's thirteen factors, as a reader names them —
 * the one label map every style card (fund, portfolio, groups, risk) reads.
 * Written out rather than only read off `fundamentalStyleFactorModel`, so a
 * card that has not loaded the model yet, or never asks for it, names the
 * factors the same way. Matches the model's own labels (KI-IN-EQUITY-RISK-
 * MODEL-1.0).
 */
export const STYLE_FACTOR_LABELS = {
  value: 'Value',
  earnings_yield: 'Earnings Yield',
  size: 'Size',
  short_term_momentum: 'Short-Term Momentum',
  momentum: 'Momentum (Medium-Term)',
  long_term_momentum: 'Momentum (Long-Term)',
  volatility: 'Volatility',
  leverage: 'Leverage',
  growth: 'Growth',
  liquidity: 'Liquidity',
  dividend_yield: 'Dividend Yield',
  profitability: 'Profitability',
  quality: 'Quality',
};

/**
 * A style factor's label: the map above, then the model's own label for a
 * factor the map does not know yet, then the name as sent — an industry or
 * market factor keeps its own.
 */
export const styleFactorLabel = (name, modelFactors = null) => STYLE_FACTOR_LABELS[name]
  ?? modelFactors?.find((f) => f.name === name)?.label
  ?? name;

/**
 * The holdings behind one style factor's exposure, largest pull first.
 *
 * `rows` are FundamentalStyleFactorHoldingContributionType — stored or
 * computed, the same shape. A row's value for `factor` is its contribution
 * (weight × the stock's own z-score), and across holdings they sum to the
 * entity's exposure to that factor.
 */
export function holdingContributors(rows, factor) {
  return (rows ?? [])
    .map((r) => ({
      symbol: r.holdingSecuritySymbol,
      weight: Number(r.weight) || 0,
      value: finite((r.contributions ?? []).find((c) => c.factor === factor)?.exposure),
    }))
    .filter((r) => r.value != null)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
}

/**
 * The same contributors as HoldingDrill rows: named, and the weight as a
 * percentage of the book rather than the fraction the model stores.
 */
export const styleDrillRows = (rows, factor, nameOf = (s) => s) => holdingContributors(rows, factor)
  .map((r) => ({ ...r, name: nameOf(r.symbol), weight: r.weight * 100 }));

/**
 * `quality_score` → `Quality score`.
 *
 * The engine names a factor in snake_case, and a table that prints the key
 * raw reads like a dump of the row it came from. One spelling, wherever a
 * factor is named: the key as a sentence, the word "score" kept — it is part
 * of what the number is, a loading rather than a weight.
 */
export const factorLabel = (name) => {
  const words = String(name || '').replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : '—';
};

/**
 * `low_volatility_score` → `Low volatility`, for a column header.
 *
 * The same name with the word dropped, and only where the reading already says
 * what the numbers are — a matrix whose every column is a factor score says it
 * once above the table rather than in each of eight headings.
 */
export const factorShortLabel = (name) => factorLabel(String(name || '').replace(/_score$/, ''));

/**
 * Whether a key the factor payload carries is a factor at all.
 *
 * `r_squared` rides along in the same map and is not a tilt: it is how much of
 * the movement the factor model explained, which says how much to trust the
 * loadings beside it rather than being one of them. In a table of tilts sorted
 * by how far each moved it reads as a factor the plan changed, which it is not.
 */
export const isFactorTilt = (name) => !/^r[\s_]?squared$/i.test(String(name || '').trim());

const num = (v) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));
const addOrNull = (a, b) => (a == null && !b ? null : (a ?? 0) + (b || 0));

export function styleFactorsOnly(res) {
  if (!res || !Array.isArray(res.factors)) return res;

  const style = res.factors.filter((f) => isStyleFactor(f.factor));
  const folded = res.factors.filter((f) => !isStyleFactor(f.factor));
  const foldedEffect = folded.reduce((t, f) => t + (num(f.factorEffect) ?? 0), 0);
  const styleTotal = style.some((f) => num(f.factorEffect) != null)
    ? style.reduce((t, f) => t + (num(f.factorEffect) ?? 0), 0)
    : num(res.factorTotal);

  const holdings = (res.holdings ?? []).map((h) => {
    const cells = h.factors ?? [];
    const out = cells.filter((c) => !isStyleFactor(c.factor)).reduce((t, c) => t + (num(c.factorEffect) ?? 0), 0);
    return {
      ...h,
      factors: cells.filter((c) => isStyleFactor(c.factor)),
      residualReturn: addOrNull(num(h.residualReturn), out),
    };
  });

  return {
    ...res,
    factors: style,
    holdings,
    factorTotal: styleTotal,
    residualReturn: addOrNull(num(res.residualReturn), foldedEffect),
    /** The sector / industry factors folded into the residual, by name. */
    foldedFactors: folded.map((f) => f.factor),
  };
}
